import type { ContentType } from '@lb/domain'
import { LOCALE_PREFIX, splitLocalePath } from '@lb/i18n'

/**
 * The ONE place the API's URL segments and our routes are joined.
 *
 * The backend refuses to mint links into our routing, and it is right to: it
 * "cannot know another codebase's URL structure and cannot be told when it
 * changes", and a link minted there goes stale silently and 404s on click with
 * nothing in either codebase looking wrong. They shipped that bug once
 * (`/content/reading/{id}` against an admin serving `/readings/{id}`) and the
 * fix was to delete the field.
 *
 * So what they give us is a fact about THEIR routing — `CONTENT_PATH`, on
 * `ContentSummary.path`, `CollectionMember.path` and every search hit — and
 * this is where we translate it into a fact about ours. Nowhere else in the app
 * concatenates a segment into a URL.
 */

/** Their segment → our route prefix. A full record: a new type must be placed. */
const ROUTE_BY_API_PATH: Record<string, string | null> = {
  courses: '/resources/courses',
  readings: '/resources/readings',
  audios: '/resources/audios',
  printables: '/resources/printables',
  collections: '/resources/collections',
  podcasts: '/podcasts',
  qanda: '/qanda',
  'case-studies': '/cases',
  events: '/events',
  /*
   * Explicit nulls rather than missing keys — these have no page of their own.
   * A phrase is rendered inline on Home and a challenge lives at `/challenge`
   * without an id, so linking to one by id would produce a route that does not
   * exist. `null` says "decided, not forgotten", which a missing key cannot.
   */
  phrases: null,
  challenges: null,
}

/**
 * Our route for one item, or null when the type has no page of its own.
 *
 * TWO spellings, because the app has two kinds of anchor and they want
 * opposite things. A raw `<a href>` (ContentCard) needs the locale prefix —
 * the browser follows it alone, and an unprefixed path drops an English
 * session into French. The router's `<Link to>` must NOT have it — the
 * router's basepath already carries `/en`, so a prefixed `to` renders
 * `/en/en/…` and lands on Not Found. That double prefix shipped on every
 * Link-rendered card (podcasts, Q&A, browse, search, the resume cards) and
 * only on English, because French's canonical prefix is empty.
 */
export function routeForContent(apiPath: string, id: string): string | null {
  const path = routePathForContent(apiPath, id)
  return path ? localizedHref(path) : null
}

/** The app-relative spelling, for `<Link to>` — the basepath adds the locale. */
export function routePathForContent(apiPath: string, id: string): string | null {
  const prefix = ROUTE_BY_API_PATH[apiPath]
  return prefix ? `${prefix}/${id}` : null
}

/** The same mapping from a `ContentType`, for callers holding the enum instead. */
const API_PATH_BY_TYPE: Record<ContentType, string> = {
  COURSE: 'courses',
  COLLECTION: 'collections',
  READING: 'readings',
  AUDIO: 'audios',
  PRINTABLE: 'printables',
  PODCAST: 'podcasts',
  QANDA: 'qanda',
  PHRASE: 'phrases',
  EVENT: 'events',
  CASE_STUDY: 'case-studies',
}

export function routeForType(type: ContentType, id: string): string | null {
  return routeForContent(API_PATH_BY_TYPE[type], id)
}


/**
 * Our route per API segment. The API never mints links into our routing.
 *
 * Lives here rather than beside the Library screen because both the filtered
 * grid and the browse shelves need it, and having the shelves import from the
 * screen that renders them would be a cycle.
 */
export const ROUTE_FOR: Record<
  'courses' | 'readings' | 'audios' | 'printables' | 'collections' | 'podcasts',
  string
> = {
  // An episode keeps its own page; only the LIST moved into the Library (18 Sep 2026).
  podcasts: '/podcasts',
  courses: '/resources/courses',
  readings: '/resources/readings',
  audios: '/resources/audios',
  printables: '/resources/printables',
  collections: '/resources/collections',
}

/*
 * Prepend the current locale basepath to a route.
 *
 * ContentCard renders a plain `<a href>` (it lives in `@lb/ui`, which is
 * route-library-agnostic on purpose), so clicking one triggers a full
 * document load. When we're on `/en/…` and the href is
 * `/resources/courses/{id}`, the browser navigates to the un-prefixed
 * path, the router boots with basepath `undefined`, and the app comes
 * up in French. Every card click on the English library flipped the
 * whole session back to French, reported live.
 *
 * The right fix is to build the href WITH the prefix at the callsite,
 * once, using the same helpers the router uses when it boots. Cheaper
 * than passing a `LinkComponent` through ContentCard's contract.
 */
export function localizedHref(href: string): string {
  const prefix = LOCALE_PREFIX[splitLocalePath(window.location.pathname).locale]
  return prefix ? `${prefix}${href}` : href
}
